import { Link } from "react-router-dom";
import {
  PlayCircle, Building2, ReceiptText, Users, FileText, Plug, Gauge, ScrollText, Bell, Wallet, ArrowRight, Download,
} from "lucide-react";

// Explainer video (direct MP4 + poster). Update to swap the walkthrough.
const EXPLAINER_VIDEO_URL = "https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/video/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/introducing_retainproof_the_ultimate_tool_for_trac.mp4";
const EXPLAINER_VIDEO_POSTER = "https://d3uryq9bhgb5qr.cloudfront.net/FreeTrial/Google_102980368733255228124/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106/image/0a83f95f-dd5d-44f2-ad3d-c2202b9eb106.jpg";

// Full client onboarding walkthrough (~3.5 min). Swap the URL to replace with a screen-recorded version.
const TUTORIAL_VIDEO_URL = "/retainproof-tutorial.mp4";
const TUTORIAL_VIDEO_POSTER = "/retainproof-tutorial-poster.jpg";
const API = process.env.REACT_APP_BACKEND_URL;

// Detailed, self-serve written walkthrough shown under the video.
const walkthrough = [
  { n: "01", title: "Confirm your Firm Profile", to: "/app/profile",
    enter: ["Firm legal name, office locations and primary practice areas", "Intake software / CRM and call-tracking systems you use", "Approx. attorneys and intake staff"],
    read: ["This profile scopes your whole workspace — vendors, channels and locations", "Click Save; you can update it anytime as the firm changes"] },
  { n: "02", title: "Add Vendors & Contracts", to: "/app/vendors",
    enter: ["Each vendor: name, category (agency, ad platform, directory, call tracker)", "Monthly retainer and any ad-spend commitment", "Contract renewal / cancellation deadline (critical)"],
    read: ["RetainProof raises 30 / 60 / 90-day alerts before auto-renewal", "Vendors added here become selectable when you upload invoices"] },
  { n: "03", title: "Reconcile invoices with AI", to: "/app/invoices",
    enter: ["Pick the vendor, then upload the invoice (PDF or photo)", "Repeat each billing cycle as new invoices arrive"],
    read: ["AI extracts the total and audits each line against the contract", "Flags duplicate charges, unapproved increases and out-of-period fees", "Ends in a RISK verdict: LOW / MEDIUM / HIGH; original file is stored"] },
  { n: "04", title: "Log leads & signed cases", to: "/app/leads",
    enter: ["Add each inquiry, or bulk-import a CSV (source, stage, revenue)", "Move leads through inquiry → qualified → consultation → signed", "On signed cases, record the matter revenue"],
    read: ["This powers your true cost per retained case and channel ROI", "Use additional touch sources for multi-touch attribution"] },
  { n: "05", title: "Read your Overview", to: "/app",
    enter: ["Nothing to enter — this reads from the data above"],
    read: ["North-star: cost per retained case (top of the page)", "Budget vs. actual spend and the lead-to-case funnel", "Revenue by channel plus contract & overspend alerts"] },
  { n: "06", title: "Scorecards, Alerts & Executive Report", to: "/app/scorecards",
    enter: ["Open Alerts & Digest to enable the weekly email", "Open Executive Report and click Generate for a monthly briefing"],
    read: ["Scorecards rank vendors by spend, signed cases, cost/case and ROI", "The Executive Report exports a partner-ready PDF in one click"] },
  { n: "07", title: "Connect your data sources", to: "/app/integrations",
    enter: ["Connect CallRail, Meta Ads or Clio using your own API keys", "Use Sync now to pull data; Reconnect to rotate a token"],
    read: ["Calls, ad spend and signed matters then flow in automatically", "Google Ads connection is rolling out"] },
];

const steps = [
  { n: "01", title: "Create your firm", body: "You're the Owner. Set up your plan through guided enrollment from Billing.", to: "/app/billing", cta: "Go to Billing" },
  { n: "02", title: "Add your vendors", body: "Enter each vendor's retainer and — crucially — the cancellation deadline so we warn you before auto-renewal.", to: "/app/vendors", cta: "Add vendors" },
  { n: "03", title: "Reconcile an invoice", body: "Upload a vendor invoice (PDF/photo). AI audits it against the contract and flags overbilling with a RISK verdict.", to: "/app/invoices", cta: "Upload an invoice" },
  { n: "04", title: "Log leads & signed cases", body: "Add leads or import a CSV, then mark the ones you signed with revenue — that powers cost per retained case.", to: "/app/leads", cta: "Open Lead-to-Case" },
  { n: "05", title: "Read your Overview", body: "See cost-per-case, budget vs. actual, the funnel, revenue by channel, and contract/overspend alerts.", to: "/app", cta: "View Overview" },
];

const modules = [
  { icon: Wallet, name: "Budget Center", body: "Budgeted vs. actual by practice area, office, channel or vendor.", to: "/app/budgets" },
  { icon: Building2, name: "Vendors & Contracts", body: "Contracts, retainers and 30/60/90-day renewal alerts.", to: "/app/vendors" },
  { icon: Users, name: "Lead-to-Case", body: "Funnel from inquiry to signed case, with revenue.", to: "/app/leads" },
  { icon: ReceiptText, name: "Invoice Reconciliation", body: "AI audit of vendor invoices against contract terms.", to: "/app/invoices" },
  { icon: Gauge, name: "Vendor Scorecards", body: "Compare vendors on spend, signed cases, cost/case and ROI.", to: "/app/scorecards" },
  { icon: ScrollText, name: "Compliance Archive", body: "Advertising approvals, disclaimers and run dates by jurisdiction.", to: "/app/compliance" },
  { icon: Bell, name: "Alerts & Weekly Digest", body: "Proactive alerts plus an AI-written weekly email.", to: "/app/alerts" },
  { icon: FileText, name: "Executive Report", body: "One-click, partner-ready monthly marketing briefing.", to: "/app/report" },
  { icon: Plug, name: "Integrations", body: "Connect CallRail, Meta Ads or Clio with your own API keys.", to: "/app/integrations" },
];

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Getting started</p>
        <h1 className="font-display text-4xl font-light mt-1">Resources &amp; Help</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Everything you need to get your firm's marketing under control — watch the walkthrough,
          follow the quick start, or jump straight into any module.
        </p>
        <a href={`${API}/api/public/getting-started.pdf`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 border border-zinc-300 hover:border-[#0B1F33] px-4 h-10 text-sm font-medium text-[#0B1F33] transition-colors" data-testid="download-cheatsheet">
          <Download className="h-4 w-4" /> Download the one-page Getting-Started PDF
        </a>
      </div>

      {/* Full walkthrough video */}
      <div className="border border-zinc-200 bg-white p-6 mb-6" data-testid="resources-tutorial">
        <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#B54747] mb-3">Complete client walkthrough · ~3.5 min</p>
        <video
          controls
          preload="metadata"
          poster={TUTORIAL_VIDEO_POSTER}
          className="w-full border border-zinc-200 bg-black"
          data-testid="resources-tutorial-player"
        >
          <source src={TUTORIAL_VIDEO_URL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <p className="text-sm text-zinc-500 mt-3">
          A step-by-step tour of the full workflow after enrollment — setting up your firm, adding vendors,
          reconciling invoices, logging cases, and reading every report. Prefer to read? Follow the written guide below.
        </p>
      </div>

      {/* Explainer video */}
      <div className="border border-zinc-200 bg-white p-6 mb-8" data-testid="resources-video">
        <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-3">90-second intro</p>
        {EXPLAINER_VIDEO_URL ? (
          <video
            controls
            preload="metadata"
            poster={EXPLAINER_VIDEO_POSTER}
            className="w-full border border-zinc-200 bg-black"
            data-testid="resources-video-player"
          >
            <source src={EXPLAINER_VIDEO_URL} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className="flex flex-col items-center justify-center bg-[#0B1F33] text-white text-center py-16 px-6" data-testid="resources-video-placeholder">
            <PlayCircle className="h-12 w-12 text-[#D5A44A]" strokeWidth={1.4} />
            <p className="mt-4 font-display text-2xl">Your walkthrough goes here</p>
            <p className="mt-2 text-sm text-zinc-400 max-w-md">
              Record the explainer video (script in your marketing package), then paste its embed URL into
              <span className="font-mono-data text-[#D5A44A]"> EXPLAINER_VIDEO_URL</span> in Resources.jsx to make it live.
            </p>
          </div>
        )}
      </div>

      {/* Quick start */}
      <h2 className="font-display text-2xl mb-4">Quick start in 5 steps</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {steps.map((s) => (
          <div key={s.n} className="border border-zinc-200 bg-white p-6 flex flex-col" data-testid={`resource-step-${s.n}`}>
            <span className="font-mono-data text-xs text-[#D5A44A]">{s.n}</span>
            <h3 className="font-display text-xl mt-1">{s.title}</h3>
            <p className="text-sm text-zinc-600 mt-2 flex-1">{s.body}</p>
            <Link to={s.to} className="mt-4 inline-flex items-center gap-1 text-sm text-[#0B1F33] font-medium hover:gap-2 transition-all" data-testid={`resource-step-link-${s.n}`}>
              {s.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ))}
      </div>

      {/* Module reference */}
      <h2 className="font-display text-2xl mb-4">Every module, explained</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map((m) => (
          <Link
            key={m.name}
            to={m.to}
            data-testid={`resource-module-${m.name.toLowerCase().replace(/\W+/g, "-")}`}
            className="group border border-zinc-200 bg-white p-6 hover:border-[#0B1F33] transition-colors"
          >
            <m.icon className="h-6 w-6 text-[#B54747]" strokeWidth={1.6} />
            <h3 className="font-display text-lg mt-3 flex items-center gap-1">
              {m.name}
              <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </h3>
            <p className="text-sm text-zinc-600 mt-1">{m.body}</p>
          </Link>
        ))}
      </div>

      {/* Detailed written walkthrough */}
      <h2 className="font-display text-2xl mt-12 mb-2">Step-by-step walkthrough</h2>
      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">Exactly what to enter in each area and how to read the results — the written companion to the video above.</p>
      <div className="space-y-4 mb-10" data-testid="resources-walkthrough">
        {walkthrough.map((s) => (
          <div key={s.n} className="border border-zinc-200 bg-white p-6" data-testid={`walkthrough-step-${s.n}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="font-mono-data text-sm text-[#D5A44A]">{s.n}</span>
                <h3 className="font-display text-xl">{s.title}</h3>
              </div>
              <Link to={s.to} className="shrink-0 inline-flex items-center gap-1 text-sm text-[#0B1F33] font-medium hover:gap-2 transition-all" data-testid={`walkthrough-link-${s.n}`}>
                Open <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-4">
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-400 mb-2">What to enter</p>
                <ul className="space-y-1.5">
                  {s.enter.map((it) => (
                    <li key={it} className="flex gap-2 text-sm text-zinc-700"><span className="text-[#D5A44A] mt-0.5">›</span>{it}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-400 mb-2">What you'll see</p>
                <ul className="space-y-1.5">
                  {s.read.map((it) => (
                    <li key={it} className="flex gap-2 text-sm text-zinc-700"><span className="text-[#0E7C7B] mt-0.5">✓</span>{it}</li>
                  ))}
                </ul>
              </div>
            </div>
            <video controls preload="none" className="w-full mt-4 border border-zinc-200 bg-black" data-testid={`walkthrough-clip-${s.n}`}>
              <source src={`/retainproof-clip-${s.n}.mp4`} type="video/mp4" />
            </video>
          </div>
        ))}
      </div>

      <p className="mt-10 text-xs text-zinc-400 font-mono-data" data-testid="resources-footer">
        Need a hand? Request an Executive Marketing Audit at /consultation · RetainProof™ by CMD+DesignLab
      </p>
    </div>
  );
}
